import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useConfig } from "@/hooks/useConfig";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Trash2, Film, FileText, Image as ImageIcon } from "lucide-react";
import { money, pretty, compressPhoto, isPhotoFile, sizeText, PHOTO_HARD_LIMIT, VIDEO_LIMIT } from "./claimsShared";

export default function NewClaimPage() {
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const claimTypes = getValues("claim_types");
  const priorities = getValues("claim_priorities");

  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderLines, setOrderLines] = useState([]);
  const [picked, setPicked] = useState({});
  const [form, setForm] = useState({
    customer_id: "", order_id: "", claim_type: "", priority: "normal",
    complaint_received_at: "", goods_received_at: "", description: "",
    near_expiry_report_month: "", late_reason: "",
  });
  const [orderMeta, setOrderMeta] = useState(null);
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data?.customers || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setOrders([]); setOrderLines([]); setPicked({}); setOrderMeta(null);
    if (!form.customer_id) return;
    api.get(`/claims/lookup/orders?customer_id=${form.customer_id}`)
      .then((r) => setOrders(r.data)).catch(() => {});
  }, [form.customer_id]);

  useEffect(() => {
    setOrderLines([]); setPicked({});
    if (!form.order_id) return;
    api.get(`/claims/lookup/order-lines/${form.order_id}`)
      .then((r) => { setOrderLines(r.data.lines || []); setOrderMeta(r.data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [form.order_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const windowHours = 48;
  const isLate = (() => {
    if (!form.goods_received_at || form.claim_type === "near_expiry") return false;
    const grd = new Date(form.goods_received_at);
    return (Date.now() - grd.getTime()) / 36e5 > windowHours;
  })();

  const linePayload = () =>
    Object.entries(picked)
      .filter(([, v]) => Number(v.qty) > 0)
      .map(([sku_id, v]) => ({ sku_id, qty_claimed: Number(v.qty), batch_number: v.batch || "" }));

  const claimedTotal = Object.entries(picked).reduce((sum, [sku, v]) => {
    const line = orderLines.find((l) => l.sku_id === sku);
    return sum + (line ? Number(v.qty || 0) * Number(line.rate || 0) : 0);
  }, 0);

  const saveDraft = async () => {
    setBusy(true);
    try {
      const body = { ...form, lines: linePayload() };
      if (claim) {
        const { data } = await api.put(`/claims/${claim._id}`, body);
        setClaim(data);
        toast.success("Draft saved");
      } else {
        const { data } = await api.post("/claims", body);
        setClaim(data);
        toast.success(`Draft ${data.claim_id} saved`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save the draft");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!claim) { toast.error("Save the draft first, then add photos"); return; }
    setUploading(true);
    try {
      let toSend = file;
      if (isPhotoFile(file)) {
        toSend = await compressPhoto(file);
        if (toSend.size > PHOTO_HARD_LIMIT) {
          toast.error(`${file.name} is still ${sizeText(toSend.size)}. A photo must be under 2 MB. Please take the photo again.`);
          return;
        }
      } else if (file.size > VIDEO_LIMIT) {
        toast.error(`${file.name} is ${sizeText(file.size)}. A video must be under 50 MB.`);
        return;
      }
      const fd = new FormData();
      fd.append("file", toSend, file.name);
      await api.post(`/claims/${claim._id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/claims/${claim._id}`);
      setClaim(data);
      toast.success(`${file.name} added (${sizeText(toSend.size)})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add that file");
    } finally { setUploading(false); }
  };

  const removeEvidence = async (fileId) => {
    await api.delete(`/claims/${claim._id}/evidence/${fileId}`).catch(() => {});
    const { data } = await api.get(`/claims/${claim._id}`);
    setClaim(data);
  };

  const submit = async () => {
    if (!claim) { toast.error("Save the draft first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/claims/${claim._id}/submit`, { late_reason: form.late_reason });
      toast.success(`${data.claim_id} submitted`);
      navigate(`/claims/${data._id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally { setBusy(false); }
  };

  const evidence = claim?.evidence || [];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="new-claim-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900">
          Raise Claim {claim ? <span className="font-mono text-sm text-zinc-500">({claim.claim_id})</span> : null}
        </h1>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={saveDraft} disabled={busy} data-testid="save-draft-btn">Save Draft</Button>
          <Button size="sm" onClick={submit} disabled={busy || !claim} data-testid="submit-claim-btn">Send for Check</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 bg-white border border-zinc-200 rounded-lg p-4">
        <div>
          <Label className="text-xs">Customer</Label>
          <select className="w-full h-9 border border-zinc-200 rounded px-2 text-xs" value={form.customer_id}
            onChange={(e) => set("customer_id", e.target.value)} data-testid="claim-customer-select">
            <option value="">Select customer…</option>
            {customers.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Order</Label>
          <select className="w-full h-9 border border-zinc-200 rounded px-2 text-xs" value={form.order_id}
            onChange={(e) => set("order_id", e.target.value)} data-testid="claim-order-select">
            <option value="">Select order…</option>
            {orders.map((o) => (<option key={o._id} value={o._id}>{o.order_number} — {o.order_date}</option>))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Claim type</Label>
          <select className="w-full h-9 border border-zinc-200 rounded px-2 text-xs" value={form.claim_type}
            onChange={(e) => set("claim_type", e.target.value)} data-testid="claim-type-select">
            <option value="">Select type…</option>
            {claimTypes.map((t) => (<option key={t} value={t}>{pretty(t)}</option>))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Priority</Label>
          <select className="w-full h-9 border border-zinc-200 rounded px-2 text-xs" value={form.priority}
            onChange={(e) => set("priority", e.target.value)} data-testid="claim-priority-select">
            {(priorities.length ? priorities : ["urgent", "normal", "low"]).map((p) => (
              <option key={p} value={p}>{pretty(p)}</option>))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Complaint Date &amp; Time</Label>
          <Input type="datetime-local" className="h-9 text-xs" value={form.complaint_received_at}
            onChange={(e) => set("complaint_received_at", e.target.value)} data-testid="claim-complaint-at" />
        </div>
        <div>
          <Label className="text-xs">Goods Received Date</Label>
          <Input type="date" className="h-9 text-xs" value={form.goods_received_at}
            onChange={(e) => set("goods_received_at", e.target.value)} data-testid="claim-goods-received-at" />
          {orderMeta && (
            <p className="text-[11px] text-zinc-500 mt-1" data-testid="claim-order-dates">
              Order dispatched {orderMeta.dispatch_date || "—"} · delivered {orderMeta.delivery_date || "—"}
            </p>
          )}
        </div>
        {form.claim_type === "near_expiry" && (
          <div>
            <Label className="text-xs">Stock report month</Label>
            <Input type="month" className="h-9 text-xs" value={form.near_expiry_report_month}
              onChange={(e) => set("near_expiry_report_month", e.target.value)} data-testid="claim-report-month" />
          </div>
        )}
        <div className="md:col-span-3">
          <Label className="text-xs">What happened?</Label>
          <Textarea className="text-xs" rows={2} value={form.description}
            onChange={(e) => set("description", e.target.value)} data-testid="claim-description" />
        </div>
      </div>

      {isLate && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="late-reason-box">
          <p className="text-xs font-semibold text-amber-900">This claim is outside the {windowHours}-hour window — it will need Admin approval.</p>
          <Label className="text-xs mt-2 block">Why is it late? (at least 10 characters)</Label>
          <Textarea className="text-xs" rows={2} value={form.late_reason}
            onChange={(e) => set("late_reason", e.target.value)} data-testid="claim-late-reason" />
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-line-picker">
        <h2 className="text-base font-semibold text-zinc-900 mb-2">Claim Lines</h2>
        {orderLines.length === 0 && <p className="text-xs text-zinc-400">Pick a customer and an order to list its SKUs.</p>}
        {orderLines.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                {["SKU", "Invoiced", "Already Claimed", "Rate (incl. GST)", "Claim Qty", "Batch", "Claim Amount"].map((h) => (
                  <th key={h} className="text-left px-2 py-2 font-semibold">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {orderLines.map((l) => {
                const p = picked[l.sku_id] || {};
                return (
                  <tr key={l.sku_id} className="border-t border-zinc-100" data-testid={`line-${l.sku_id}`}>
                    <td className="px-2 py-2 font-medium">{l.sku_name}<div className="text-[10px] text-zinc-400 font-mono">{l.sku_id}</div></td>
                    <td className="px-2 py-2">{l.qty_invoiced}</td>
                    <td className="px-2 py-2">{l.qty_already_claimed || 0}</td>
                    <td className="px-2 py-2">{money(l.rate)}</td>
                    <td className="px-2 py-2">
                      <Input type="number" min="0" className="h-8 w-20 text-xs" value={p.qty || ""}
                        onChange={(e) => setPicked((s) => ({ ...s, [l.sku_id]: { ...s[l.sku_id], qty: e.target.value } }))}
                        data-testid={`line-qty-${l.sku_id}`} />
                    </td>
                    <td className="px-2 py-2">
                      {l.batches?.length > 0 ? (
                        <select className="h-8 border border-zinc-200 rounded px-1 text-xs" value={p.batch || ""}
                          onChange={(e) => setPicked((s) => ({ ...s, [l.sku_id]: { ...s[l.sku_id], batch: e.target.value } }))}
                          data-testid={`line-batch-${l.sku_id}`}>
                          <option value="">Select batch…</option>
                          {l.batches.map((b) => (<option key={b} value={b}>{b}</option>))}
                        </select>
                      ) : (
                        <div>
                          <Input className="h-8 w-28 text-xs" placeholder="Batch no." value={p.batch || ""}
                            onChange={(e) => setPicked((s) => ({ ...s, [l.sku_id]: { ...s[l.sku_id], batch: e.target.value } }))}
                            data-testid={`line-batch-${l.sku_id}`} />
                          <span className="text-[10px] text-amber-700">batch not on order</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 font-medium">{money(Number(p.qty || 0) * Number(l.rate || 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {orderLines.length > 0 && (
          <p className="text-right text-sm font-semibold text-zinc-900 mt-2" data-testid="claimed-total">
            Claim Amount {money(claimedTotal)}
          </p>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="claim-evidence-box">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-zinc-900">Photos &amp; Videos</h2>
          <label className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 cursor-pointer" data-testid="evidence-upload-label">
            <Upload className="w-4 h-4" /> {uploading ? "Adding…" : "Add Photo / Video / Report"}
            <input type="file" className="hidden" data-testid="evidence-file-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          </label>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          Photos are made smaller on your phone before they go up — keep them under 2 MB. Videos
          mp4/mov/webm up to 50 MB. You need at least one photo AND one video to send the claim
          (near-expiry claims may use a stock report instead).
        </p>
        <div className="flex gap-3 flex-wrap">
          {evidence.length === 0 && <span className="text-xs text-zinc-400">Nothing added yet.</span>}
          {evidence.map((e) => (
            <div key={e.file_id} className="w-32 border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50" data-testid={`evidence-item-${e.kind}`}>
              {e.kind === "photo" && <img src={e.file_url} alt={e.file_name} loading="lazy" className="w-full h-20 object-cover" />}
              {e.kind === "video" && (
                <video src={e.file_url} preload="none" className="w-full h-20 object-cover bg-black" controls data-testid="evidence-video-preview" />
              )}
              {e.kind === "report" && <div className="h-20 flex items-center justify-center"><FileText className="w-7 h-7 text-zinc-400" /></div>}
              <div className="px-2 py-1 flex items-center justify-between gap-1">
                <span className="text-[10px] truncate flex items-center gap-1">
                  {e.kind === "photo" ? <ImageIcon className="w-3 h-3" /> : e.kind === "video" ? <Film className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                  {e.file_name}
                </span>
                <button onClick={() => removeEvidence(e.file_id)} data-testid={`evidence-remove-${e.file_id}`}>
                  <Trash2 className="w-3 h-3 text-red-500" />
                </button>
              </div>
              <div className="px-2 pb-1 text-[10px] text-zinc-400" data-testid={`evidence-size-${e.file_id}`}>{sizeText(e.size)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
